/**
 * Putting dummy data into somebody else's controlled inputs.
 *
 * Every field a task fills belongs to a React component that owns its value
 * (`value={formData.name} onChange={...}`), so this module exists to make a
 * programmatic write indistinguishable from typing.
 */

import { resolveAnchor, type AnchorSpec } from '../engine/anchors';

/** First VISIBLE match for a selector, or null. Shares the tour's own resolver. */
export function findVisible(selector: string): HTMLElement | null {
  return resolveAnchor({ selector })?.element ?? null;
}

/**
 * Wait for an anchor to turn up, then click it. Resolves to whether it clicked.
 *
 * 🔴 THIS IS WHAT MAKES A FLOW DRIVE ITSELF, and it has to wait rather than
 * clicking straight away: a step's target is routinely created by the step before
 * it — a sidebar group opening, a route committing, a dialog mounting — and every
 * one of those is asynchronous. Clicking on the same tick as the request that
 * creates the element hits nothing.
 *
 * Frame-driven so an element that is already there costs nothing at all.
 *
 * A dispatched click is unaffected by `pointer-events: none`, so this keeps
 * working even while some other layer is suppressing real pointer input.
 */
export function clickWhenReady(spec: AnchorSpec, timeoutMs = 4000): Promise<boolean> {
  return new Promise((resolve) => {
    const startedAt = Date.now();
    const tick = () => {
      const hit = resolveAnchor(spec)?.element;
      if (hit) {
        hit.click();
        return resolve(true);
      }
      if (Date.now() - startedAt >= timeoutMs) return resolve(false);
      window.requestAnimationFrame(tick);
    };
    tick();
  });
}

/**
 * A press that a Radix trigger actually believes in: the full
 * pointerdown → mousedown → pointerup → mouseup → click sequence a mouse
 * produces, rather than the bare `click` event `.click()` dispatches.
 *
 * 🔴 THIS IS WHY "ADD BLOCK JUST HIGHLIGHTED AND NOTHING WAS ADDED".
 * `@radix-ui/react-dropdown-menu`'s trigger opens the menu from `onPointerDown`
 * and `onKeyDown` — it has NO `onClick` at all (verified in
 * `@radix-ui/react-dropdown-menu/dist/index.mjs`: the trigger's only pointer
 * handler is `onPointerDown: … context.onOpenToggle()`). So `element.click()`
 * dispatched a click that Radix never listens for: the menu stayed shut, the
 * "Text answer" item never existed, and the whole add sequence bailed out with
 * only the ring around Add block to show for it. A Radix `Select` trigger is the
 * same shape (`handleOpen` on pointerdown for a mouse), which is what
 * `chooseFirstOption` above is working around by hand.
 *
 * Ordinary buttons are unaffected — they only ever look at the trailing `click`,
 * which is still dispatched, last, exactly as a real press would.
 *
 * `PointerEvent` is constructed when the environment has it and a `MouseEvent`
 * carrying the same fields stands in when it does not (jsdom, which the tour's
 * own tests run under, has no `PointerEvent` constructor).
 */
function pointerish(type: string, init: MouseEventInit & { pointerId?: number }): Event {
  // 🔴 NO `view`. Nothing in a Radix handler reads it, and jsdom rejects a
  // `PointerEvent` whose `view` came from a different realm than its own
  // constructor — which is exactly the shape a test environment hands us, so
  // including it would make every one of these throw under test while working
  // in a browser.
  const full = {
    bubbles: true,
    cancelable: true,
    composed: true,
    button: 0,
    ...init,
  };
  if (typeof PointerEvent === 'function') {
    return new PointerEvent(type, { ...full, pointerId: init.pointerId ?? 1, pointerType: 'mouse', isPrimary: true });
  }
  const event = new MouseEvent(type, full);
  Object.defineProperty(event, 'pointerId', { get: () => init.pointerId ?? 1 });
  Object.defineProperty(event, 'pointerType', { get: () => 'mouse' });
  return event;
}

/** The centre of an element, so a synthetic press carries plausible coordinates. */
function centreOf(el: HTMLElement): { clientX: number; clientY: number } {
  const rect = el.getBoundingClientRect();
  return { clientX: Math.round(rect.left + rect.width / 2), clientY: Math.round(rect.top + rect.height / 2) };
}

/**
 * Move the pointer onto an element without pressing it.
 *
 * Radix opens a submenu on `pointermove` over its trigger (after a 100ms
 * intent delay) and highlights the item under the pointer — so sending this
 * first is what makes the automated walk through the menu LOOK like someone
 * moving down it, instead of items blinking out of existence.
 */
export function hoverElement(el: HTMLElement): void {
  const at = centreOf(el);
  el.dispatchEvent(pointerish('pointerover', at));
  el.dispatchEvent(pointerish('pointerenter', { ...at, bubbles: false }));
  el.dispatchEvent(pointerish('pointermove', at));
  el.dispatchEvent(new MouseEvent('mouseover', { bubbles: true, cancelable: true, ...at }));
  el.dispatchEvent(new MouseEvent('mousemove', { bubbles: true, cancelable: true, ...at }));
}

/** Press an element the way a mouse does. See `pointerish` for why `.click()` is not enough. */
export function pressElement(el: HTMLElement): void {
  const at = centreOf(el);
  el.dispatchEvent(pointerish('pointerdown', { ...at, buttons: 1 }));
  el.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true, buttons: 1, ...at }));
  el.dispatchEvent(pointerish('pointerup', { ...at, buttons: 0 }));
  el.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true, ...at }));
  // Last, and still a real `.click()`: it is what fires React's `onClick`, which
  // is how a Radix menu ITEM (and every ordinary button) is activated.
  el.click();
}

/**
 * Wait for an anchor to turn up, then press it like a mouse would.
 *
 * `clickWhenReady`'s counterpart for anything Radix owns. Same waiting
 * behaviour and the same "resolves to whether it happened" contract; the only
 * difference is the event sequence — see `pressElement`.
 */
export function pressWhenReady(spec: AnchorSpec, timeoutMs = 4000): Promise<boolean> {
  return new Promise((resolve) => {
    const startedAt = Date.now();
    const tick = () => {
      const hit = resolveAnchor(spec)?.element;
      if (hit) {
        hoverElement(hit);
        pressElement(hit);
        return resolve(true);
      }
      if (Date.now() - startedAt >= timeoutMs) return resolve(false);
      window.requestAnimationFrame(tick);
    };
    tick();
  });
}

/** Wait for an anchor to exist. Resolves to whether it turned up. */
export function waitForAnchor(spec: AnchorSpec, timeoutMs = 4000): Promise<boolean> {
  return new Promise((resolve) => {
    const startedAt = Date.now();
    const tick = () => {
      if (resolveAnchor(spec)) return resolve(true);
      if (Date.now() - startedAt >= timeoutMs) return resolve(false);
      window.requestAnimationFrame(tick);
    };
    tick();
  });
}

/** Resolve after a fixed pause. For giving an in-flight host-app fetch a beat to settle. */
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/**
 * Poll an arbitrary predicate until it turns true, or give up. Resolves to
 * whether it did.
 *
 * For a condition `resolveAnchor` cannot express alone — e.g. "we have
 * actually landed on this route AND its button exists" (`clickWhenReady`
 * confirms a click was DISPATCHED, not that whatever it triggers has
 * finished: a route change is asynchronous, and checking only "did the click
 * fire" lets a caller move on before the destination has actually rendered).
 */
export function waitUntil(predicate: () => boolean, timeoutMs = 4000): Promise<boolean> {
  return new Promise((resolve) => {
    const startedAt = Date.now();
    const tick = () => {
      if (predicate()) return resolve(true);
      if (Date.now() - startedAt >= timeoutMs) return resolve(false);
      window.requestAnimationFrame(tick);
    };
    tick();
  });
}

type Field = HTMLInputElement | HTMLTextAreaElement;

/** First visible input/textarea for a selector, or null. */
export function findField(selector: string): Field | null {
  const el = findVisible(selector);
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return el;
  return null;
}

/**
 * The prototype's own `value` setter for this element.
 *
 * 🔴 `el.value = x` IS NOT ENOUGH, and the reason is not obvious.
 *
 * React installs its own `value` property on each mounted input, wrapping a
 * `_valueTracker` that remembers the last value it knows about. Assigning
 * through that property updates the tracker too — so when the `input` event
 * arrives React compares new-value against tracked-value, sees no difference,
 * and DISCARDS THE EVENT. The field would show the text and `formData` would
 * stay empty, which then fails validation on submit with an error pointing at a
 * field the user can plainly see is filled in.
 *
 * Going through the prototype setter writes the DOM value while leaving the
 * tracker stale, which is exactly the state a real keystroke produces.
 *
 * The prototype has to match the element: `HTMLInputElement.prototype`'s setter
 * throws on a textarea.
 */
function nativeValueSetter(el: Field): ((value: string) => void) | null {
  const proto =
    el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  return setter ? (value: string) => setter.call(el, value) : null;
}

export interface FillOptions {
  /**
   * Leave a field that already has something in it.
   *
   * Defaults to TRUE, and that default is load-bearing: a step is re-entered
   * every time the user presses Back, and overwriting would silently throw away
   * whatever they had typed since. It also means a field the app pre-fills
   * itself (the composer defaults the footer to "Reply STOP to unsubscribe")
   * keeps the app's own value rather than being restated by the tour.
   */
  onlyIfEmpty?: boolean;
}

/**
 * Type a value into a field. Returns whether anything was written.
 *
 * Dispatches `input` and `change`, both bubbling, because React attaches one
 * listener at the root and only sees events that reach it. `blur` follows so the
 * composer's `onBlur` marks the field touched — that is what lets its inline
 * validation confirm the value is acceptable, which is the whole point of
 * showing the user a filled-in form.
 */
export function fillField(
  selector: string,
  value: string,
  options: FillOptions = {},
): boolean {
  const el = findField(selector);
  return el ? fillElement(el, value, options) : false;
}

/**
 * `fillField` against an element already in hand.
 *
 * 🔴 SPLIT OUT FOR CONTROLS THAT HAVE NO SELECTOR AT ALL. A field row the flow
 * has just added to the form builder is found by POSITION inside a freshly
 * mounted accordion — there is no id, no test id, and any attribute the flow
 * writes onto it is thrown away the moment React re-keys the row (which the
 * builder does as soon as a label commits, since a block's key is derived from
 * its name). Passing the element straight through is the only way to write into
 * one without inventing a hook that would not survive the next render.
 */
export function fillElement(el: Field, value: string, { onlyIfEmpty = true }: FillOptions = {}): boolean {
  if (el.disabled || el.readOnly) return false;
  if (onlyIfEmpty && el.value.trim() !== '') return false;

  const setValue = nativeValueSetter(el);
  if (!setValue) return false;

  setValue(value);
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));
  // Focus first so the blur is real: blurring an element that never had focus
  // dispatches nothing, and the touched flag would stay unset.
  el.focus();
  el.blur();
  return true;
}

/** Does this field hold anything? False when it does not exist at all. */
export function isFilled(selector: string): boolean {
  return (findField(selector)?.value ?? '').trim() !== '';
}

/**
 * Open a picker and choose its first available option.
 *
 * For the app's `SearchableSelect`, which is a Popover wrapping a `cmdk` list —
 * so the options are real elements with `role="option"` and a plain `.click()`
 * selects one. That is why this is safe to automate while a Radix `Select` is not:
 * a Radix `Select` opens on a synthetic pointer sequence its trigger inspects,
 * whereas this trigger is an ordinary button.
 *
 * Returns whether an option was chosen. Never throws — the caller treats failure
 * as "the user picks it", which is a working outcome rather than a broken one.
 */
export function chooseFirstOption(
  triggerSpec: AnchorSpec,
  { timeoutMs = 3000, optionSelector = '[role="option"]' } = {},
): Promise<boolean> {
  const trigger = resolveAnchor(triggerSpec)?.element;
  if (!trigger) return Promise.resolve(false);

  // Only open it if it is closed; the trigger toggles, so clicking an already
  // open picker would shut it.
  if (trigger.getAttribute('data-state') !== 'open') trigger.click();

  return new Promise((resolve) => {
    const startedAt = Date.now();
    const tick = () => {
      const option = Array.from(document.querySelectorAll<HTMLElement>(optionSelector)).find(
        (el) => el.getAttribute('aria-disabled') !== 'true' && el.getAttribute('data-disabled') === null,
      );
      if (option) {
        option.click();
        return resolve(true);
      }
      if (Date.now() - startedAt >= timeoutMs) return resolve(false);
      window.requestAnimationFrame(tick);
    };
    tick();
  });
}

/**
 * Has a value been chosen in a trigger that shows its placeholder when empty?
 *
 * For the app's `SearchableSelect`, whose trigger renders
 * `{selected?.label || placeholder}` — so comparing against the placeholder is
 * the only signal available from the DOM. Deliberately NOT a "is the text
 * non-empty" test: the placeholder is real text and would pass one.
 */
export function isChosen(selector: string, placeholder: string): boolean {
  const trigger = findVisible(selector);
  if (!trigger) return false;
  const text = (trigger.textContent ?? '').trim();
  return text !== '' && text !== placeholder.trim();
}

/**
 * Is this checkbox ticked? Reads Radix's `aria-checked` before a native
 * `.checked`, because the app's `Checkbox` is a `<button role="checkbox">` and has
 * no `checked` property at all.
 */
export function isChecked(selector: string): boolean {
  const el = findVisible(selector);
  if (!el) return false;
  const aria = el.getAttribute('aria-checked');
  if (aria !== null) return aria === 'true';
  return el instanceof HTMLInputElement ? el.checked : false;
}

/**
 * Tick a checkbox if it is not already ticked. Returns whether it ended up on.
 *
 * A plain `.click()` is right here — unlike a Radix `Select`, a Radix `Checkbox`
 * toggles on a normal click, so there is no synthetic pointer sequence to get
 * wrong. Guarded on the current state because clicking an already-ticked box
 * would turn it OFF.
 */
export function ensureChecked(selector: string): boolean {
  if (isChecked(selector)) return true;
  const el = findVisible(selector);
  if (!el || (el as HTMLButtonElement).disabled) return false;
  el.click();
  return isChecked(selector);
}

/**
 * Does this Radix `SelectTrigger` show a chosen value rather than its placeholder?
 *
 * Radix marks an unfilled trigger's `<SelectValue>` with `data-placeholder`,
 * which is a far better signal than reading the text — the placeholder ("Select
 * category") is real text and would otherwise count as a value.
 */
export function isSelected(selector: string): boolean {
  const trigger = findVisible(selector);
  if (!trigger) return false;
  if (trigger.querySelector('[data-placeholder]')) return false;
  return (trigger.textContent ?? '').trim() !== '';
}
